import { $t } from '@/i18n';
import store from '@/store';
import { useRouter } from '@/router/useRouter';
import { isEqual } from 'lodash';
import { shallAIPageKeepAlive } from '../utils/keep-alive';
import pageLoader from '@loaders/page/loader';
export class SyncPageBatchError extends Error {
    failures;
    constructor(failures) {
        super(`保存失败：${failures.length} 个页面同步失败`);
        this.name = 'SyncPageBatchError';
        this.failures = failures;
        Object.setPrototypeOf(this, SyncPageBatchError.prototype);
    }
}
function getAIPageIdByPath(path) {
    return (`aipage-${path}`).replace(/[^a-zA-Z0-9-_]+/g, '-').replace(/^-+|-+$/g, '')
        .replace(/--+/g, '-');
}
function normalizeError(error) {
    return error instanceof Error ? error : new Error(String(error));
}
async function preparePageSyncPlan(result, options = {}) {
    const resultPages = result?.meta?.pages || [];
    const { getAiPageDetailList, allKindsPageBasicInfoList, projectId } = await prepareEnv(options);
    const aiPageDetailList = await getAiPageDetailList();
    const reviewedPages = new Map((options.pageChanges || [])
        .filter(change => change.compareData?.type === 'ai')
        .map(change => [change.id, change.compareData]));
    let lastAIPage = null;
    const oldAIPageList = [];
    const pathToList = new Map();
    const pageIdToList = new Map();
    for (const basicPageInfo of allKindsPageBasicInfoList || []) {
        const { path } = basicPageInfo;
        if (!path)
            continue;
        const page = aiPageDetailList.find(p => p.path === path) || basicPageInfo; // AI 页面需要完整信息？
        pathToList.set(path, page);
        if (page.pageId)
            pageIdToList.set(page.pageId, page);
        if (page.type === 'ai') {
            oldAIPageList.push(page);
            if (!lastAIPage || lastAIPage.mtime < page.mtime) {
                lastAIPage = page;
            }
        }
    }
    const desiredPathSet = new Set();
    const duplicatePaths = new Set();
    const desiredPageIdToPaths = new Map();
    for (const page of resultPages) {
        const path = page.routePath;
        if (desiredPathSet.has(path))
            duplicatePaths.add(path);
        desiredPathSet.add(path);
        const pageId = getAIPageIdByPath(path);
        const paths = desiredPageIdToPaths.get(pageId) || [];
        paths.push(path);
        desiredPageIdToPaths.set(pageId, paths);
    }
    const duplicatePageIds = Array.from(desiredPageIdToPaths.entries())
        .map(([pageId, paths]) => [pageId, Array.from(new Set(paths))])
        .filter(([, paths]) => paths.length > 1);
    if (duplicatePaths.size || duplicatePageIds.length) {
        const details = [
            ...Array.from(duplicatePaths).map(path => $t('k_176d681e', { arg_0: path })),
            ...duplicatePageIds.map(([pageId, paths]) => $t('k_987eef87', { arg_0: pageId, arg_1: paths.join(', ') })),
        ];
        throw new Error([$t('k_446752f6'), ...details].join('\n'));
    }
    const defaultLayoutType = lastAIPage?.layoutType || 'header-sidebar-content';
    // 从首页 prompt 新建的项目：项目内尚无任何 AI 页面时，第一个创建的页面默认用「内容」布局
    const isFirstPageInNewAiProject = lastAIPage === null;
    let firstNewPageLayoutTypeUsed = false;
    // 同步页面
    const syncTasks = [];
    const usedOldAIPageIds = new Set();
    const reservedBackupPaths = new Set();
    const pageToWuji2Info = (page, refOldWujiPage, layoutTypeOverride) => {
        const path = page.routePath;
        const pageId = getAIPageIdByPath(path);
        const keepAlive = shallAIPageKeepAlive(path, page.meta);
        const pageAttr = {
            navFixTop: 'system',
            customNav: 'system',
            customSidebar: 'system',
            customHeader: 'system',
            customFooter: 'system',
            showWatermark: 'system',
            keepAlive,
            isMobile: false,
            ...(typeof refOldWujiPage?.pageAttr === 'string' ? JSON.parse(refOldWujiPage.pageAttr) : refOldWujiPage?.pageAttr ?? {}),
        };
        const title = page.meta?.title || refOldWujiPage?.name || (path === '/' ? $t('k_bfad681e') : path);
        if (page.meta?.description)
            pageAttr.metaDescription = page.meta.description;
        if (page.meta?.metaKeywords)
            pageAttr.metaKeywords = page.meta.metaKeywords;
        return {
            pageTag: null,
            modalConfig: '[]',
            dataSource: '[]',
            pageType: 'content',
            dataSourceRelation: '[]',
            pageData: '{"selectSchemas":[],"schemaConfig":[]}',
            pageModalConfig: '[]',
            sourceCodeConfig: '{}',
            isOffline: 0,
            // 平台配置不属于 Git 产物。合并时保留审阅后的属性；普通保存沿用原规则。
            ...(options.pageChanges ? refOldWujiPage : {}),
            id: `${projectId}:${pageId}`,
            pageId,
            projectId,
            name: `${title}`,
            title,
            layoutType: layoutTypeOverride ?? refOldWujiPage?.layoutType ?? defaultLayoutType,
            path,
            type: 'ai',
            pageConfig: JSON.stringify({ hash: page.hash }),
            pageAttr: JSON.stringify(pageAttr),
        };
    };
    const toSyncPageInfo = (page) => ({
        pageId: page.pageId,
        name: page.name || page.title || page.path || page.pageId,
        path: page.path || '',
        title: page.title,
    });
    const toJSONIfNotObject = (value) => {
        if (typeof value === 'object') {
            return JSON.stringify(value);
        }
        return value;
    };
    const getAvailableBackupPath = (path) => {
        for (let i = 0; i < 100; i++) {
            const newPath = i === 0 ? `${path}-backup` : `${path}-backup-${i}`;
            if (!pathToList.get(newPath) && !desiredPathSet.has(newPath) && !reservedBackupPaths.has(newPath)) {
                reservedBackupPaths.add(newPath);
                return newPath;
            }
        }
        throw new Error($t('k_102bb28f', { arg_0: path }));
    };
    const throwPageIdOccupiedError = (path, pageId, occupiedPage) => {
        throw new Error($t('k_b2357a57', {
            arg_0: path,
            arg_1: pageId,
            arg_2: occupiedPage.name || occupiedPage.pageId,
            arg_3: occupiedPage.path || '-',
        }));
    };
    // 查缺补漏
    for (const page of resultPages) {
        const path = page.routePath;
        const existPage = pathToList.get(path);
        const reviewedPage = reviewedPages.get(`${projectId}:${getAIPageIdByPath(path)}`)
            || (existPage && reviewedPages.get(existPage.id || `${projectId}:${existPage.pageId}`));
        const referencePage = reviewedPage || existPage;
        const isFirstNewPage = isFirstPageInNewAiProject && !existPage && !firstNewPageLayoutTypeUsed;
        const layoutTypeOverride = isFirstNewPage && !referencePage?.layoutType ? 'content' : undefined;
        if (isFirstNewPage)
            firstNewPageLayoutTypeUsed = true;
        const wuji2Info = pageToWuji2Info(page, referencePage, layoutTypeOverride);
        const samePageIdPage = pageIdToList.get(wuji2Info.pageId);
        if (!existPage) {
            if (samePageIdPage) {
                if (samePageIdPage.type === 'ai' && samePageIdPage.path && !desiredPathSet.has(samePageIdPage.path)) {
                    usedOldAIPageIds.add(samePageIdPage.pageId);
                    syncTasks.push({
                        operation: 'update',
                        page: toSyncPageInfo(wuji2Info),
                        localPage: wuji2Info,
                        remotePage: samePageIdPage,
                    });
                    continue;
                }
                throwPageIdOccupiedError(path, wuji2Info.pageId, samePageIdPage);
            }
            // 新建
            syncTasks.push({
                operation: 'create',
                page: toSyncPageInfo(wuji2Info),
                localPage: wuji2Info,
            });
        }
        else if (existPage.type !== 'ai') {
            if (options.allowNonAIPageBackup === false) {
                throw new Error($t('k_500c37dc', { arg_0: path }));
            }
            // 已经存在
            // 但是不是AI页面
            // 修改存量页面的路径
            const newPath = getAvailableBackupPath(path);
            if (samePageIdPage) {
                if (samePageIdPage !== existPage && samePageIdPage.type === 'ai' && samePageIdPage.path && !desiredPathSet.has(samePageIdPage.path)) {
                    usedOldAIPageIds.add(samePageIdPage.pageId);
                    syncTasks.push({
                        operation: 'update',
                        page: toSyncPageInfo(existPage),
                        localPage: { ...existPage, path: newPath },
                        remotePage: existPage,
                    });
                    syncTasks.push({
                        operation: 'update',
                        page: toSyncPageInfo(wuji2Info),
                        localPage: wuji2Info,
                        remotePage: samePageIdPage,
                    });
                    continue;
                }
                throwPageIdOccupiedError(path, wuji2Info.pageId, samePageIdPage);
            }
            syncTasks.push({
                operation: 'update',
                page: toSyncPageInfo(existPage),
                localPage: { ...existPage, path: newPath },
                remotePage: existPage,
            });
            syncTasks.push({
                operation: 'create',
                page: toSyncPageInfo(wuji2Info),
                localPage: wuji2Info,
            });
        }
        else {
            // 存在同path的是AI页面
            usedOldAIPageIds.add(existPage.pageId);
            // 可能旧的 pageId 是错误的，如果这样就需要删除了，然后重新创建
            if (existPage.pageId !== wuji2Info.pageId) {
                if (samePageIdPage) {
                    throwPageIdOccupiedError(path, wuji2Info.pageId, samePageIdPage);
                }
                syncTasks.push({
                    operation: 'delete',
                    page: toSyncPageInfo(existPage),
                    remotePage: existPage,
                });
                syncTasks.push({
                    operation: 'create',
                    page: toSyncPageInfo(wuji2Info),
                    localPage: wuji2Info,
                });
                continue;
            }
            // 否则就按需增量修改内容
            // 如果 title 需要同步...
            const needSync = (wuji2Info.name !== existPage.name)
                || Boolean(reviewedPage)
                || wuji2Info.layoutType !== existPage.layoutType
                || !isEqual(toJSONIfNotObject(wuji2Info.pageConfig), toJSONIfNotObject(existPage.pageConfig))
                || !isEqual(toJSONIfNotObject(wuji2Info.pageAttr), toJSONIfNotObject(existPage.pageAttr));
            if (needSync) {
                syncTasks.push({
                    operation: 'update',
                    page: toSyncPageInfo(wuji2Info),
                    localPage: wuji2Info,
                    remotePage: existPage,
                });
            }
        }
    }
    // 删除多余
    for (const page of oldAIPageList) {
        if (usedOldAIPageIds.has(page.pageId))
            continue;
        syncTasks.push({
            operation: 'delete',
            page: toSyncPageInfo(page),
            remotePage: page,
        });
    }
    return {
        projectId,
        tasks: syncTasks,
        managedPageIds: new Set([
            ...oldAIPageList.map(page => page.id || `${projectId}:${page.pageId}`),
            ...resultPages.map(page => `${projectId}:${getAIPageIdByPath(page.routePath)}`),
        ]),
    };
}
export async function syncPageListByGogo(result) {
    const { projectId, tasks } = await preparePageSyncPlan(result);
    const failures = [];
    for (const task of tasks) {
        try {
            if (task.operation === 'create') {
                await store.dispatch('createPage', task.localPage);
            }
            else if (task.operation === 'update') {
                await store.dispatch('updatePage', { localPage: task.localPage, remotePage: task.remotePage });
            }
            else {
                await store.dispatch('deletePage', { projectId, pageId: task.remotePage?.pageId });
            }
        }
        catch (error) {
            failures.push({
                operation: task.operation,
                page: task.page,
                error: normalizeError(error),
            });
        }
    }
    if (failures.length > 0) {
        throw new SyncPageBatchError(failures);
    }
}
export async function preparePageMergeChangesByGogo(result, options) {
    const { projectId, tasks, managedPageIds } = await preparePageSyncPlan(result, {
        ...options,
        pageChanges: options.pageChanges || [],
        // 旧 merge 接口会并行写同一 schema，无法安全地先让路再创建；直接要求用户解决路径冲突。
        allowNonAIPageBackup: false,
    });
    return {
        managedPageIds,
        changes: tasks.map((task) => {
            const page = task.operation === 'delete' ? task.remotePage : task.localPage;
            return {
                id: page?.id || `${projectId}:${page?.pageId}`,
                schemaInfo: { id: '_wuji_xy:page' },
                compareData: task.operation === 'delete' ? null : page,
            };
        }),
    };
}
let basicPageListInflight = null;
function fetchBasicPageListDeduped(requestParams) {
    const key = JSON.stringify(requestParams);
    if (basicPageListInflight?.key === key) {
        return basicPageListInflight.promise;
    }
    const promise = store.dispatch('fetchBasicPageList', requestParams).finally(() => {
        if (basicPageListInflight?.key === key) {
            basicPageListInflight = null;
        }
    });
    basicPageListInflight = { key, promise };
    return promise;
}
async function prepareEnv(options = {}) {
    const { route } = useRouter();
    const projectId = options.projectId || route.value?.query.projectid;
    const branchId = options.branchId || route.value?.query.branch;
    // 拉取页面列表
    const requestParams = {
        projectId,
        query: {
            size: 'total',
        },
        env: 'dev',
    };
    if (branchId)
        requestParams.branchId = branchId;
    const basicPageList = await fetchBasicPageListDeduped(requestParams);
    const getAiPageDetailList = async () => {
        const pages = await pageLoader.loadList(projectId, { query: { size: 'total', filter: '(type="ai")' } }, 'dev', branchId);
        return pages || [];
    };
    return {
        allKindsPageBasicInfoList: basicPageList, // 所有页面，包括低代码的
        getAiPageDetailList,
        projectId,
    };
}
export async function prepareInstructionsForPagesDir() {
    const { allKindsPageBasicInfoList } = await prepareEnv();
    // 1. find all non-ai pages and their paths into existingPath: string[]
    const conflictedPaths = [];
    for (const page of allKindsPageBasicInfoList || []) {
        if (page.type !== 'ai' && page.path) {
            conflictedPaths.push(page.path);
        }
    }
    // 1.1 check if already has
    const hasRoot = store.state.project?.projectInfo?.homePage?.path;
    if (hasRoot && conflictedPaths.includes(hasRoot)) {
        conflictedPaths.push('/'); // root page bound to a lowcode page
    }
    if (!conflictedPaths.includes('/403')) {
        conflictedPaths.push('/403');
    }
    // 2. convert into fileNames: string[]   like ["/src/pages/index.vue", "/src/pages/about.vue"]
    // special care about / -> /index.vue
    const fileNames = conflictedPaths.map((path) => {
        // Handle root path
        if (path === '/') {
            return '/src/pages/index.vue';
        }
        // Handle other paths: /about -> /src/pages/about.vue
        return `/src/pages${path}.vue`;
    });
    // 3. if fileNames is empty, return ''
    if (fileNames.length === 0) {
        return null;
    }
    // 4. if fileNames is not empty, return formatted message
    const pathsStr = conflictedPaths.map(path => `- "${path}"`).join('\n');
    const filesStr = fileNames.map(fileName => `- ${fileName} (can't access! reserved by platform)`).join('\n');
    const prompt = `These route paths are reserved by low-code, please do not use them: \n${pathsStr}\nHence, these files are reserved, DON'T READ OR WRITE THEM:\n${filesStr}\nYou can manage pages with other names.`;
    return {
        prompt,
        reservedPaths: fileNames,
    };
}
